import { invalidate } from '$app/navigation';
import { page } from '$app/state';
import { processStreamedResponse } from '$lib/process-streamed-response';
import { handleApiError } from '@astlai/common/util/error-handling';
import { Fetchable } from '@astlai/common/util/fetchable';
import { nid } from '@astlai/common/util/nanoid';
import { COL_CONVERSATION } from 'src/constants/collections';
import { PbService } from 'src/services/pb-service.svelte';
import type { ConversationRecord } from 'src/types/conversation';
import {
	ChatMessage,
	type ChunkDTO,
	type DocumentOverview,
	type QuotedDocument,
	type QuotedWebSource,
	type RetrievalContext,
	type WebSourceDocument,
	type SourceDocument
} from 'src/types/llm';
import {
	extractCitationsWithPositions,
	extractWebCitationsWithPositions,
	transformMessage,
	type ExtractedCitation,
	type ExtractedWebCitation,
	type InlineQuote
} from 'src/util/citation-transformer';
import { getHighlightColor, getWebHighlightColor } from 'src/util/colors';
import { mergeRetrievalContext, mergeWebSources, mergeSources } from 'src/util/merge-sources';
import { getContext, setContext } from 'svelte';

export enum ChatStep {
	INTERVIEW,
	IMAGE_GENERATION,
	UPLOAD_INTENTION,
	UPLOAD_CONFIRMATION,
	UPLOAD_FINISHED
}

export class ChatContext {
	private initialMessage = `Hallo, wie kann ich dir helfen?`;
	readonly fetchable = new Fetchable();

	conversation: ConversationRecord = $state();
	webSearchEnabled: boolean = $state(page.data.loggedInUser?.settings?.webSearchEnabled ?? false);
	standalone: boolean = $state(false);
	chatPlaceholder: string | undefined = $state(undefined);
	private currentAbortController: AbortController | null = $state(null);
	readonly isStreaming = $derived(this.currentAbortController !== null);

	readonly presentableMessages = $derived(
		this.conversation?.messages.filter(
			(m) => (m.role !== 'system' && !!m.content) || !!m.reasoning_content
		)
	);

	readonly latestAssistantMessage = $derived(
		this.conversation?.messages.toReversed().find((m) => m.role === 'assistant')
	);

	readonly isReasoning = $derived(
		!!this.latestAssistantMessage &&
			this.latestAssistantMessage.status !== 'completed' &&
			!!this.latestAssistantMessage.reasoning_content &&
			!this.latestAssistantMessage.content
	);

	// Extract citations with exact positions - single source of truth for all citation data
	readonly extractedCitations: ExtractedCitation[] = $derived(
		extractCitationsWithPositions(
			this.conversation?.messages?.map((m) => ({ id: m.id, content: m.content })) ?? []
		)
	);

	readonly extractedWebCitations: ExtractedWebCitation[] = $derived(
		extractWebCitationsWithPositions(
			this.conversation?.messages?.map((m) => ({ id: m.id, content: m.content })) ?? []
		)
	);

	readonly quotedWebSources: QuotedWebSource[] = $derived.by(() => {
		if (!this.conversation?.web_sources?.length || !this.extractedWebCitations.length) {
			return [];
		}

		const citedIds = new Set(this.extractedWebCitations.map((c) => c.webId));

		return this.conversation.web_sources
			.filter((s) => citedIds.has(s.id))
			.filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i)
			.map((s, i) => {
				const citation_color = getWebHighlightColor(i);
				const citation_index = i + 1;

				const sourceCitations = this.extractedWebCitations.filter((c) => c.webId === s.id);
				const shouldAddSubIndices = sourceCitations.length > 1;
				const inlineQuotes: InlineQuote[] = sourceCitations.map((citation, idx) => ({
					citation_index,
					citation_color,
					sub_index: shouldAddSubIndices ? String.fromCharCode(65 + idx) : '',
					quotedText: citation.quote,
					messageId: citation.messageId,
					startIndex: citation.startIndex,
					length: citation.length,
					cid: s.id,
					citationType: 'web'
				}));

				return { ...s, citation_color, citation_index, inlineQuotes };
			});
	});

	readonly quotedDocuments: QuotedDocument[] = $derived.by(() => {
		if (!this.conversation || !this.conversation.messages) {
			return [];
		}

		const referencedCIDs = new Set(this.extractedCitations.map((c) => c.cid));

		return this.conversation.sources
			.filter((sc) => [...sc.chunk_ids, sc.doc_id].some((id) => referencedCIDs.has(id)))
			.map((sc, i) => {
				const citation_color = getHighlightColor(i);
				const citation_index = i + 1;

				// Find all citations that reference this so422urce's chunks or doc_id
				const sourceIds = new Set([...sc.chunk_ids, sc.doc_id]);
				const sourceCitations = this.extractedCitations.filter((c) => sourceIds.has(c.cid));

				// Create InlineQuotes with position info, assigning sub_indices
				const shouldAddSubIndices = sourceCitations.length > 1;
				const inlineQuotes: InlineQuote[] = sourceCitations.map((citation, idx) => ({
					citation_index,
					citation_color,
					sub_index: shouldAddSubIndices ? String.fromCharCode(65 + idx) : '', // A, B, C...
					quotedText: citation.quote,
					chunkText: this.chunkTexts.get(citation.cid),
					// Position info for replacement
					messageId: citation.messageId,
					startIndex: citation.startIndex,
					length: citation.length,
					cid: citation.cid,
					citationType: 'document'
				}));

				return {
					...sc,
					citation_color,
					citation_index,
					inlineQuotes
				};
			});
	});

	// Flatten all quotes for easy iteration during transformation
	readonly allInlineQuotes: InlineQuote[] = $derived([
		...this.quotedDocuments.flatMap((source) => source.inlineQuotes),
		...this.quotedWebSources.flatMap((source) => source.inlineQuotes)
	]);

	readonly transformMessage = $derived((message: string, messageId: string) =>
		transformMessage(this.allInlineQuotes, message, messageId)
	);

	// Lookup maps for documents and chunks
	readonly documentMap = $derived(
		new Map<number, DocumentOverview>(
			(this.conversation?.retrievalContext?.documents ?? []).map((d) => [d.id, d])
		)
	);

	readonly chunkMap = $derived(
		new Map<number, ChunkDTO>(
			(this.conversation?.retrievalContext?.chunks ?? []).map((c) => [c.id, c])
		)
	);

	readonly chunkTexts = $derived(
		new Map<number, string>(
			(this.conversation?.retrievalContext?.chunks ?? []).map((c) => [c.id, c.text])
		)
	);

	private apiClient: PbService;

	constructor(baseUrl?: string, apiKey?: string, initialConvo?: ConversationRecord) {
		// we need a specific instance of pbservice here, because it might be instantiated by the standalone component
		this.apiClient = new PbService(baseUrl, apiKey);
		this.conversation = initialConvo;
		$effect(() => {
			this.webSearchEnabled = page.data.loggedInUser?.settings?.webSearchEnabled ?? false;
		});
	}

	/**
	 * Get a completion from the llm, an additional message can be appended. this message will not automatically appear in the message history
	 * @param additionalMessage
	 * @returns
	 */
	private async getCompletion(additionalMessage?: ChatMessage, appendResponse = false) {
		const tempId = nid();
		let accMessage: ChatMessage = {
			created: new Date(),
			id: tempId,
			status: 'pending',
			content: '',
			reasoning_content: '',
			role: 'assistant',
			type: 'message'
		};
		if (appendResponse) {
			this.appendMessage(accMessage);
		}

		// Create abort controller for this request
		const abortController = new AbortController();
		this.currentAbortController = abortController;

		try {
			const response = await this.apiClient.chatRequest(
				this.conversation.id,
				additionalMessage
					? [...this.conversation.messages, additionalMessage]
					: this.conversation.messages,
				this.conversation.model,
				this.conversation.embeddingsVendor,
				this.conversation.tenant,
				abortController.signal,
				this.webSearchEnabled
			);

			await handleApiError(response);

			const stream = processStreamedResponse(response, accMessage, abortController);

			for await (const {
				eventData,
				eventType,
				message,
				sources,
				webSources,
				retrievalContext
			} of stream) {
				if (eventType === 'conversation.created') {
					this.conversation.id = eventData.id as string;
				}
				if (retrievalContext) {
					this.setRetrievalContext(retrievalContext);
				}
				if (sources) {
					this.appendSources(sources);
				}
				if (webSources) {
					this.appendWebSources(webSources);
				}
				if (eventType === 'rag.topic') {
					const { topic_description } = eventData as { topic_description: string };
					if (!this.conversation.title) {
						this.conversation.title = topic_description;
						setTimeout(() => invalidate('update:conversations'), 1000);
					}
				}
				if (eventType === 'rag.usage') {
					const { input_tokens, output_tokens } = eventData as {
						input_tokens: number;
						output_tokens: number;
					};
					this.conversation.total_input_tokens =
						(this.conversation.total_input_tokens ?? 0) + input_tokens;
					this.conversation.total_output_tokens =
						(this.conversation.total_output_tokens ?? 0) + output_tokens;
				}
				accMessage = message;
				if (appendResponse) {
					this.upsertMessage(accMessage, tempId);
				}
			}

			return accMessage;
		} catch (e) {
			// Only remove message and re-throw if it wasn't aborted by user
			const isAbortError = e instanceof Error && e.name === 'AbortError';
			if (!isAbortError) {
				this.removeMessage(tempId);
				throw e;
			}
		} finally {
			// Clear abort controller when stream completes
			this.currentAbortController = null;
		}
	}

	upsertMessage(message: ChatMessage, tempId?: string) {
		if (message.id) {
			if (tempId && tempId !== message.id) {
				// Real ID differs from temp - check if temp message still exists
				const tempExists = this.conversation.messages.some((m) => m.id === tempId);
				if (tempExists) {
					return this.updateMessage(tempId, message);
				}
				// Temp message was already replaced, fall through to update by real ID
			}
			return this.updateMessage(message.id, message);
		}

		return this.appendMessage(message);
	}

	appendMessage(message: ChatMessage) {
		const id = message.id || nid();
		this.conversation.messages = [...this.conversation.messages, { ...message, id }];
		return id;
	}

	updateMessage(id: string, messageUpdate: Partial<ChatMessage>) {
		const i = this.conversation.messages.findIndex((m) => m.id === id);
		if (i > -1) {
			this.conversation.messages[i] = { ...this.conversation.messages[i], ...messageUpdate };
			return messageUpdate.id;
		}
		this.appendMessage({ id, ...messageUpdate } as ChatMessage);
	}

	removeMessage(id: string) {
		this.conversation.messages = this.conversation.messages.filter((m) => m.id !== id);
	}

	appendSources(sources: SourceDocument[]) {
		this.conversation.sources = mergeSources([...this.conversation.sources, ...sources]);
	}

	appendWebSources(sources: WebSourceDocument[]) {
		this.conversation.web_sources = mergeWebSources([
			...(this.conversation.web_sources ?? []),
			...sources
		]);
	}

	setRetrievalContext(context: RetrievalContext) {
		this.conversation.retrievalContext = mergeRetrievalContext(
			this.conversation.retrievalContext,
			context
		);
	}

	reset() {
		this.conversation.messages = [];
		this.upsertMessage(new ChatMessage('assistant', this.initialMessage));
	}

	async chat(userMessage?: string) {
		if (userMessage) {
			this.upsertMessage(new ChatMessage('user', userMessage));
		}

		return this.getCompletion(null, true);
	}

	async abort() {
		if (this.currentAbortController) {
			this.currentAbortController.abort();

			// Mark the last assistant message as incomplete
			const lastMessage = this.conversation.messages.at(-1);
			if (lastMessage?.role === 'assistant') {
				this.updateMessage(lastMessage.id, {
					status: 'incomplete'
				});

				// Persist to database
				await this.updateConversation({
					messages: this.conversation.messages
				});
			}

			this.currentAbortController = null;
		}
	}

	async retry() {
		this.fetchable.fetch(async () => {
			if (this.conversation.messages.at(-1)?.role === 'assistant' && !!this.conversation.id) {
				this.conversation.messages.pop();
				await this.updateConversation({
					messages: this.conversation.messages
				});
			}
			this.fetchable.error = null;
			await this.chat();
		});
	}

	async updateConversation(params: Partial<ConversationRecord>) {
		this.conversation.title = params.title || this.conversation.title;
		this.conversation.model = params.model || this.conversation.model;
		this.apiClient.collection(COL_CONVERSATION).update(this.conversation.id, params);
		await invalidate(`update:conversation:${this.conversation.id}`);
		await invalidate('update:conversations');
	}
}

export const CHAT_CONTEXT_KEY = Symbol('chat');

export function getChatContext() {
	return getContext<ChatContext>(CHAT_CONTEXT_KEY);
}

export function setChatContext(ctx: ChatContext) {
	setContext(CHAT_CONTEXT_KEY, ctx);
}
